import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAutocomplete from "components/MDAutocomplete";
import DefaultStatisticsCard from "examples/Cards/StatisticsCards/DefaultStatisticsCard";
import DataTable from "examples/Tables/DataTable";
import api from "services/api";

const statusLabel = {
  BARU: "Baru",
  DIPROSES: "Diproses",
  SELESAI: "Selesai",
};

const kategoriLabel = {
  PENGADUAN: "Pengaduan",
  SARAN: "Saran",
  PERTANYAAN: "Pertanyaan",
  LAINNYA: "Lainnya",
};

const statusColor = {
  BARU: "error",
  DIPROSES: "warning",
  SELESAI: "success",
};

const STATUS_OPTIONS = [
  { value: "BARU", label: "Baru" },
  { value: "DIPROSES", label: "Diproses" },
  { value: "SELESAI", label: "Selesai" },
];

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("id-ID");
}

function TindakLanjutDialog({ open, item, onClose, onSaved }) {
  const [status, setStatus] = useState("BARU");
  const [catatan, setCatatan] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setStatus(item.status || "BARU");
      setCatatan(item.catatan_tindak_lanjut || "");
    }
  }, [item]);

  const handleSave = async () => {
    if (!item) return;
    setSaving(true);
    try {
      await api.put(`pengaduan-masukan/${item.id}/tindak-lanjut`, {
        status,
        catatan_tindak_lanjut: catatan,
      });
      onSaved();
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || "Gagal menyimpan tindak lanjut");
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Tindak Lanjut Pengaduan #{item.id}
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MDBox mb={2}>
          <MDTypography variant="button" fontWeight="bold">
            {item.subjek}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            {item.nama} • {kategoriLabel[item.kategori] || item.kategori} • {formatDate(item.created_at)}
          </MDTypography>
        </MDBox>

        <MDBox mb={2} p={2} borderRadius="lg" bgColor="grey-100">
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Isi Pengaduan
          </MDTypography>
          <MDTypography variant="body2">{item.pesan}</MDTypography>
          {(item.email || item.no_hp) && (
            <MDTypography variant="caption" color="text" display="block" mt={1}>
              Kontak: {item.email || "-"} / {item.no_hp || "-"}
            </MDTypography>
          )}
        </MDBox>

        <MDAutocomplete
          label="Status"
          fullWidth
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          sx={{ mb: 2 }}
        />

        <MDInput
          label="Catatan Tindak Lanjut"
          fullWidth
          multiline
          rows={4}
          value={catatan}
          onChange={(e) => setCatatan(e.target.value)}
          placeholder="Jelaskan tindak lanjut yang telah atau akan dilakukan..."
        />

        {item.nama_petugas && (
          <MDTypography variant="caption" color="text" display="block" mt={1}>
            Terakhir ditangani: {item.nama_petugas}
            {item.updated_at ? ` • ${formatDate(item.updated_at)}` : ""}
          </MDTypography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>
          Batal
        </MDButton>
        <MDButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
          {saving ? "Menyimpan..." : "Simpan Tindak Lanjut"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

TindakLanjutDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

function PengaduanAdminPanel({ compact = false, title = "Pengaduan & Masukan Publik" }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ baru: 0, diproses: 0, selesai: 0, total: 0 });
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadData = () => {
    setLoading(true);
    Promise.all([api.get("pengaduan-masukan/stats"), api.get("pengaduan-masukan")])
      .then(([statsRes, listRes]) => {
        setStats(statsRes.data?.data || {});
        setItems(listRes.data?.data || []);
      })
      .catch(() => alert("Gagal memuat data pengaduan"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const displayItems = compact ? items.slice(0, 5) : items;

  const columns = [
    { Header: "Tanggal", accessor: "tanggal", width: "14%" },
    { Header: "Pengadu", accessor: "pengadu", width: "16%" },
    { Header: "Kategori", accessor: "kategori", width: "12%" },
    { Header: "Subjek", accessor: "subjek", width: "22%" },
    { Header: "Status", accessor: "status", width: "12%" },
    { Header: "Tindak Lanjut", accessor: "tindak", width: "14%" },
    { Header: "Aksi", accessor: "action", align: "center" },
  ];

  const rows = displayItems.map((p) => ({
    tanggal: formatDate(p.created_at),
    pengadu: (
      <MDBox>
        <MDTypography variant="caption" fontWeight="medium" display="block">
          {p.nama}
        </MDTypography>
        <MDTypography variant="caption" color="text">
          {p.no_hp || p.email || "-"}
        </MDTypography>
      </MDBox>
    ),
    kategori: kategoriLabel[p.kategori] || p.kategori,
    subjek: p.subjek,
    status: <Chip label={statusLabel[p.status] || p.status} color={statusColor[p.status] || "default"} size="small" />,
    tindak: p.catatan_tindak_lanjut ? (
      <MDTypography variant="caption">{p.catatan_tindak_lanjut.slice(0, 60)}{p.catatan_tindak_lanjut.length > 60 ? "..." : ""}</MDTypography>
    ) : (
      <MDTypography variant="caption" color="text">Belum ada</MDTypography>
    ),
    action: (
      <MDButton
        size="small"
        color="info"
        variant="text"
        onClick={() => {
          setSelected(p);
          setDialogOpen(true);
        }}
      >
        Proses
      </MDButton>
    ),
  }));

  return (
    <>
      <MDBox mb={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={0.5}>
          {title}
        </MDTypography>
        <MDTypography variant="button" color="text">
          Kelola pengaduan dan masukan dari masyarakat melalui landing page
        </MDTypography>
      </MDBox>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <DefaultStatisticsCard title="Baru" count={loading ? "..." : stats.baru} icon={{ color: "error", component: "mark_email_unread" }} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DefaultStatisticsCard title="Diproses" count={loading ? "..." : stats.diproses} icon={{ color: "warning", component: "pending" }} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DefaultStatisticsCard title="Selesai" count={loading ? "..." : stats.selesai} icon={{ color: "success", component: "task_alt" }} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DefaultStatisticsCard title="Total" count={loading ? "..." : stats.total} icon={{ color: "info", component: "forum" }} />
        </Grid>
      </Grid>

      <Card>
        <MDBox p={3}>
          {compact && items.length > 5 && (
            <MDTypography variant="caption" color="text" display="block" mb={2}>
              Menampilkan 5 pengaduan terbaru dari {items.length} total
            </MDTypography>
          )}
          {loading ? (
            <MDTypography>Memuat pengaduan...</MDTypography>
          ) : items.length === 0 ? (
            <MDTypography color="text">Belum ada pengaduan masuk.</MDTypography>
          ) : (
            <DataTable
              table={{ columns, rows }}
              entriesPerPage={{ defaultValue: compact ? 5 : 10 }}
              canSearch={!compact}
            />
          )}
        </MDBox>
      </Card>

      <TindakLanjutDialog
        open={dialogOpen}
        item={selected}
        onClose={() => {
          setDialogOpen(false);
          setSelected(null);
        }}
        onSaved={loadData}
      />
    </>
  );
}

PengaduanAdminPanel.propTypes = {
  compact: PropTypes.bool,
  title: PropTypes.string,
};

export default PengaduanAdminPanel;
